import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { useLocale } from '../../app/hooks';
import { useStaffGrant } from '../../api/queries';
import type { StaffGrant } from '../../api/types';
import { Alert, Button } from '../../components/ui';
import { buttonClass } from '../../components/ui/button-class';
import { DEFAULT_TIME_ZONE, formatDateTime } from '../../lib/format';
import { ErrorAlert } from '../billing/BillingNotices';

const TZ = DEFAULT_TIME_ZONE;

/** Banner and controls of the caller's grant (also used in the workspace while it is active). */
export function GrantBanner({
  workspaceId,
  grant,
  inWorkspace,
}: {
  workspaceId: string;
  grant: Pick<StaffGrant, 'expiresAt' | 'reason'>;
  inWorkspace?: boolean;
}) {
  const { t } = useTranslation();
  const locale = useLocale();
  const end = useStaffGrant(workspaceId);
  return (
    <Alert tone="warning" role="status" title={t('staff.access.activeTitle')}>
      <p data-testid="staff-readonly-banner">
        {t('staff.access.activeBody', {
          until: formatDateTime(grant.expiresAt, locale, TZ),
        })}
      </p>
      <p className="mt-0.5 text-xs">
        {t('staff.access.reasonLabel')}: {grant.reason}
      </p>
      <div className="mt-2 flex flex-wrap gap-2">
        {inWorkspace ? (
          <Link to={`/staff/workspaces/${workspaceId}`} className={buttonClass('secondary', 'sm')}>
            {t('staff.access.backToConsole')}
          </Link>
        ) : (
          <Link to={`/w/${workspaceId}`} className={buttonClass('secondary', 'sm')}>
            {t('staff.access.openWorkspace')}
          </Link>
        )}
        <Button size="sm" variant="ghost" loading={end.isPending} onClick={() => end.mutate(null)}>
          {t('staff.access.end')}
        </Button>
      </div>
      {end.error ? <ErrorAlert error={end.error} className="mt-2" /> : null}
    </Alert>
  );
}
